import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { scansApi, patientsApi, generationApi } from '../../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Loader2,
  Upload,
  Sparkles,
  Download,
  RefreshCw,
  Share2,
  QrCode,
  Heart,
  Baby,
  User,
  X,
  Clapperboard,
  Move3d
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

// Scanning animation component
const ScanningEffect = () => (
  <div className="absolute inset-0 overflow-hidden rounded-lg">
    <div className="scanning-line"></div>
    <div className="scanning-glow"></div>
    <style>{`
      .scanning-line {
        position: absolute;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, transparent, #5A67D8, #EC4899, #5A67D8, transparent);
        animation: scan 2s ease-in-out infinite;
        box-shadow: 0 0 20px #5A67D8, 0 0 40px #EC4899;
      }
      .scanning-glow {
        position: absolute;
        inset: 0;
        background: linear-gradient(180deg, rgba(90,103,216,0.1) 0%, transparent 50%, rgba(236,72,153,0.1) 100%);
        animation: pulse 1.5s ease-in-out infinite;
      }
      @keyframes scan {
        0%, 100% { top: 0; }
        50% { top: 100%; }
      }
      @keyframes pulse {
        0%, 100% { opacity: 0.3; }
        50% { opacity: 0.6; }
      }
    `}</style>
  </div>
);

// Photo upload card component
const PhotoUploadCard = ({ title, icon: Icon, image, onUpload, onRemove, color }) => (
  <div className="space-y-2">
    <Label className="flex items-center gap-2">
      <Icon className={`w-4 h-4 ${color}`} />
      {title}
    </Label>
    <div className={`border-2 border-dashed rounded-xl p-4 text-center transition-all hover:border-opacity-80 ${
      image ? 'border-transparent bg-slate-50' : 'border-slate-200 hover:border-slate-300'
    }`}>
      {image ? (
        <div className="relative">
          <img src={image} alt={title} className="w-24 h-24 mx-auto rounded-full object-cover border-4 border-white shadow-lg" />
          <button
            onClick={onRemove}
            className="absolute top-0 right-1/2 translate-x-12 -translate-y-1 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <label className="cursor-pointer block">
          <div className={`w-16 h-16 mx-auto rounded-full border-2 border-dashed flex items-center justify-center mb-2 ${color.replace('text-', 'border-')}`}>
            <Upload className={`w-6 h-6 ${color} opacity-50`} />
          </div>
          <p className="text-sm text-slate-500">Yükle</p>
          <input type="file" accept="image/*" onChange={onUpload} className="hidden" />
        </label>
      )}
    </div>
  </div>
);

// Result display with family composition and inherited traits
const FamilyResult = ({ motherImage, fatherImage, babyImage, geneticFeatures, inheritedTraits, onShare, onDownload, onWhatsApp, onQRCode }) => (
  <div className="relative">
    {/* Background gradient */}
    <div className="absolute inset-0 bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 rounded-2xl" />
    
    <div className="relative p-6 space-y-4">
      {/* Baby Image - Large and Prominent */}
      <div className="text-center">
        <div className="relative inline-block">
          <div className="absolute -inset-3 bg-gradient-to-r from-pink-400 via-purple-400 to-blue-400 rounded-2xl blur-lg opacity-40 animate-pulse" />
          <img 
            src={babyImage} 
            alt="Bebek" 
            className="relative w-48 h-48 rounded-2xl object-cover border-4 border-white shadow-2xl"
          />
        </div>
        <p className="text-lg mt-3 font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
          Bebeğiniz
        </p>
      </div>
      
      {/* Parents Row */}
      {(motherImage || fatherImage) && (
        <div className="flex justify-center items-center gap-6 py-3 border-t border-slate-200/50">
          {motherImage && (
            <div className="text-center">
              <img 
                src={motherImage} 
                alt="Anne" 
                className="w-16 h-16 rounded-full object-cover border-3 border-pink-300 shadow-md"
              />
              <p className="text-xs mt-1 text-pink-600 font-medium">Anne</p>
            </div>
          )}
          <Sparkles className="w-5 h-5 text-purple-400" />
          {fatherImage && (
            <div className="text-center">
              <img 
                src={fatherImage} 
                alt="Baba" 
                className="w-16 h-16 rounded-full object-cover border-3 border-blue-300 shadow-md"
              />
              <p className="text-xs mt-1 text-blue-600 font-medium">Baba</p>
            </div>
          )}
        </div>
      )}
      
      {/* Inherited Traits Panel */}
      {inheritedTraits && inheritedTraits.length > 0 && (
        <div className="bg-white/70 rounded-xl p-4 border border-purple-100">
          <p className="text-sm font-semibold text-purple-700 mb-2 flex items-center gap-2">
            <Heart className="w-4 h-4" />
            Genetik Özellikler
          </p>
          <div className="space-y-1">
            {inheritedTraits.map((trait, idx) => (
              <p key={idx} className="text-xs text-slate-600 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-400" />
                {trait}
              </p>
            ))}
          </div>
        </div>
      )}
      
      {/* Genetic Features Summary */}
      {geneticFeatures && (
        <div className="bg-gradient-to-r from-pink-50 to-blue-50 rounded-xl p-4 border border-purple-100">
          <p className="text-sm font-semibold text-slate-700 mb-2">Bebeğin Özellikleri</p>
          <div className="grid grid-cols-2 gap-2 text-xs">
            {(geneticFeatures.skin_tone_tr || geneticFeatures.skin_tone) && (
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{
                  backgroundColor: `rgb(${geneticFeatures.skin_rgb?.[0] || 200}, ${geneticFeatures.skin_rgb?.[1] || 170}, ${geneticFeatures.skin_rgb?.[2] || 150})`
                }} />
                <span className="text-slate-600">Ten: {geneticFeatures.skin_tone_tr || geneticFeatures.skin_tone}</span>
              </div>
            )}
            {(geneticFeatures.hair_color_tr || geneticFeatures.hair_color) && (
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-amber-800" />
                <span className="text-slate-600">Saç: {geneticFeatures.hair_color_tr || geneticFeatures.hair_color}</span>
              </div>
            )}
            {(geneticFeatures.face_shape_tr || geneticFeatures.face_shape) && (
              <div className="text-slate-600">Yüz: {geneticFeatures.face_shape_tr || geneticFeatures.face_shape}</div>
            )}
            {(geneticFeatures.hair_texture_tr || geneticFeatures.hair_texture) && (
              <div className="text-slate-600">Saç Dokusu: {geneticFeatures.hair_texture_tr || geneticFeatures.hair_texture}</div>
            )}
          </div>
        </div>
      )}
      
      {/* Share buttons */}
      <div className="flex justify-center gap-3 pt-3 border-t border-slate-200/50">
        <Button
          onClick={onDownload}
          variant="outline"
          size="sm"
          className="bg-white/80 hover:bg-white"
        >
          <Download className="w-4 h-4 mr-2 text-slate-600" />
          İndir
        </Button>
        <Button
          onClick={onWhatsApp}
          variant="outline"
          size="sm"
          className="bg-white/80 hover:bg-white text-green-600 border-green-200 hover:border-green-300"
        >
          <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
          WhatsApp
        </Button>
        <Button
          onClick={onQRCode}
          variant="outline"
          size="sm"
          className="bg-white/80 hover:bg-white"
        >
          <QrCode className="w-4 h-4 mr-2 text-slate-600" />
          QR Kod
        </Button>
      </div>
    </div>
  </div>
);

// QR Code Modal
const QRCodeModal = ({ isOpen, onClose, imageUrl, patient }) => {
  const [shareUrl, setShareUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  
  useEffect(() => {
    if (isOpen && imageUrl && !shareUrl) {
      setLoading(true);
      const token = localStorage.getItem('neobreed_token') || sessionStorage.getItem('neobreed_token');
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ image_base64: imageUrl, patient_name: patient?.first_name || '', generation_type: 'ai_face' })
      })
        .then(r => r.json())
        .then(data => setShareUrl(`${process.env.REACT_APP_BACKEND_URL}/api/share/${data.token}`))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [isOpen, imageUrl, patient, shareUrl]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 max-w-sm mx-4" onClick={e => e.stopPropagation()}>
        <div className="text-center space-y-4">
          <h3 className="text-lg font-semibold text-slate-800">QR Kod ile Paylas</h3>
          <p className="text-sm text-slate-500">Gorseli indirmek icin QR kodu tarayin</p>
          {loading ? (
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
          ) : shareUrl ? (
            <div className="bg-white p-4 rounded-lg inline-block border">
              <QRCodeSVG value={shareUrl} size={192} bgColor="#ffffff" fgColor="#1e293b" level="M" includeMargin={true} />
            </div>
          ) : null}
          {shareUrl && <p className="text-[10px] text-slate-400 break-all">{shareUrl}</p>}
          <Button onClick={onClose} variant="outline" className="w-full">
            Kapat
          </Button>
        </div>
      </div>
    </div>
  );
};

export const AIFacePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const scanId = searchParams.get('scanId');

  const [scan, setScan] = useState(null);
  const [patient, setPatient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatedImage, setGeneratedImage] = useState(null);
  
  // Photo states
  const [ultrasoundImage, setUltrasoundImage] = useState(null);
  const [ultrasoundBase64, setUltrasoundBase64] = useState(null);
  const [motherImage, setMotherImage] = useState(null);
  const [fatherImage, setFatherImage] = useState(null);
  
  // Genetic features states
  const [geneticFeatures, setGeneticFeatures] = useState(null);
  const [inheritedTraits, setInheritedTraits] = useState([]);
  
  // Modal state
  const [showQRModal, setShowQRModal] = useState(false);

  const fetchData = useCallback(async () => {
    if (!scanId) {
      toast.error('Tarama ID bulunamadı');
      navigate('/doctor/patients');
      return;
    }

    try {
      setLoading(true);
      const scanData = await scansApi.get(scanId);
      setScan(scanData);
      
      if (scanData.patient_id) {
        const patientData = await patientsApi.get(scanData.patient_id);
        setPatient(patientData);
      }
      
      if (scanData.baby_face_url) {
        setGeneratedImage(scanData.baby_face_url);
      }
    } catch (error) {
      toast.error('Veri yüklenirken hata oluştu');
      navigate('/doctor/patients');
    } finally {
      setLoading(false);
    }
  }, [scanId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleImageUpload = (setter, base64Setter = null) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      setter(event.target.result);
      if (base64Setter) {
        const base64 = event.target.result.split(',')[1];
        base64Setter(base64);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleGenerateFace = async () => {
    if (!patient) {
      toast.error('Hasta bilgisi bulunamadı');
      return;
    }

    setGenerating(true);
    setGeneticFeatures(null);
    setInheritedTraits([]);
    
    try {
      // Anne-baba fotoğraflarını base64 olarak gönder
      let motherBase64 = null;
      let fatherBase64 = null;
      
      if (motherImage) {
        motherBase64 = motherImage.includes('base64,') 
          ? motherImage.split('base64,')[1] 
          : motherImage;
      }
      
      if (fatherImage) {
        fatherBase64 = fatherImage.includes('base64,')
          ? fatherImage.split('base64,')[1]
          : fatherImage;
      }
      
      const response = await generationApi.generateAiBabyFace({
        ultrasound_base64: ultrasoundBase64,
        gender: patient.gender || 'female',
        pregnancy_week: patient.pregnancy_week || 23,
        patient_name: `${patient.first_name} ${patient.last_name}`,
        scan_id: scanId || '',
        mother_photo_base64: motherBase64,
        father_photo_base64: fatherBase64
      });

      if (response.image_base64) {
        setGeneratedImage(`data:image/png;base64,${response.image_base64}`);
        
        // Genetik özellikleri kaydet
        if (response.genetic_features) {
          setGeneticFeatures(response.genetic_features);
        }
        
        // Kalıtım özelliklerini kaydet
        if (response.inherited_traits && response.inherited_traits.length > 0) {
          setInheritedTraits(response.inherited_traits);
          toast.success(`Bebek yüzü anne-baba özelliklerinden oluşturuldu! (${response.inherited_traits.length} genetik özellik)`);
        } else if (response.genetic_features) {
          const detected = response.genetic_features.parents_detected || {};
          if (detected.mother || detected.father) {
            toast.success(`Bebek yüzü ${detected.mother && detected.father ? 'anne ve baba' : detected.mother ? 'anne' : 'baba'} özelliklerinden oluşturuldu!`);
          } else {
            toast.success('Bebek yüzü başarıyla oluşturuldu!');
          }
        } else {
          toast.success('Bebek yüzü başarıyla oluşturuldu!');
        }
        
        // Stil bilgisi varsa logla
        if (response.style_used) {
          console.log('Kullanılan stil:', response.style_used);
        }
      }
    } catch (error) {
      toast.error(error.message || 'Görsel üretimi başarısız');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!generatedImage) return;
    
    const link = document.createElement('a');
    link.href = generatedImage;
    link.download = `bebek_${patient?.first_name || 'bebek'}_${new Date().toISOString().split('T')[0]}.png`;
    link.click();
  };

  const normalizePhone = (raw) => {
    if (!raw) return '';
    let p = raw.replace(/[\s\-\(\)]/g, '').replace(/^\+/, '');
    if (p.startsWith('0')) p = '90' + p.slice(1);
    else if (!p.startsWith('90') && p.length === 10) p = '90' + p;
    return p;
  };

  const handleWhatsApp = async () => {
    if (!generatedImage) return;
    try {
      const token = localStorage.getItem('neobreed_token') || sessionStorage.getItem('neobreed_token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/share/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ image_base64: generatedImage, patient_name: patient?.first_name || '', generation_type: 'ai_face' })
      });
      const data = await res.json();
      const shareUrl = `${process.env.REACT_APP_BACKEND_URL}/api/share/${data.token}`;
      const phone = normalizePhone(patient?.phone);
      const text = encodeURIComponent(`NeoBreed AI - Bebeginizin tahmini yuzu hazir!\n\nIndirmek icin:\n${shareUrl}`);
      const waUrl = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
      window.open(waUrl, '_blank');
    } catch (e) {
      const text = `${patient?.first_name || 'Bebek'} bebeğimizin tahmini yüzü! NeoBreed AI ile oluşturuldu.`;
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="AI Bebek Yüzü">
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#5A67D8]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout 
      title="AI Bebek Yüzü Oluştur" 
      subtitle={patient ? `${patient.first_name} ${patient.last_name}` : ''}
    >
      <div className="space-y-6" data-testid="ai-face-page">
        {/* Back button */}
        <Button
          variant="ghost"
          onClick={() => navigate(`/doctor/patients/${scan?.patient_id}`)}
          className="text-slate-600 hover:text-slate-800"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Hasta Detayına Dön
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Upload Section */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100">
              <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
                <Upload className="w-5 h-5 text-[#5A67D8]" />
                Görseller
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6 p-6">
              {/* Ultrasound Upload */}
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-purple-500" />
                  Ultrason Görüntüsü (Opsiyonel)
                </Label>
                <p className="text-xs text-slate-500">Ultrason yüklerseniz AI buna göre şekillendirir</p>
                <div className={`relative border-2 border-dashed rounded-xl p-6 text-center transition-all ${
                  ultrasoundImage ? 'border-purple-300 bg-purple-50' : 'border-slate-200 hover:border-slate-300'
                }`}>
                  {ultrasoundImage ? (
                    <div className="relative">
                      <img src={ultrasoundImage} alt="Ultrason" className="max-h-40 mx-auto rounded-lg shadow-md" />
                      <button
                        onClick={() => { setUltrasoundImage(null); setUltrasoundBase64(null); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <Upload className="w-10 h-10 mx-auto text-slate-400 mb-2" />
                      <p className="text-slate-600 font-medium">Ultrason yükle</p>
                      <p className="text-xs text-slate-400 mt-1">PNG, JPG (Max 10MB)</p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload(setUltrasoundImage, setUltrasoundBase64)}
                        className="hidden"
                      />
                    </label>
                  )}
                </div>
              </div>

              {/* Parents Photos */}
              <div className="grid grid-cols-2 gap-4">
                <PhotoUploadCard
                  title="Anne Fotoğrafı"
                  icon={User}
                  image={motherImage}
                  onUpload={handleImageUpload(setMotherImage)}
                  onRemove={() => setMotherImage(null)}
                  color="text-pink-500"
                />
                <PhotoUploadCard
                  title="Baba Fotoğrafı"
                  icon={User}
                  image={fatherImage}
                  onUpload={handleImageUpload(setFatherImage)}
                  onRemove={() => setFatherImage(null)}
                  color="text-blue-500"
                />
              </div>
              
              {/* Genetic Info Banner */}
              {(motherImage || fatherImage) && (
                <div className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50 rounded-xl p-4 border border-purple-100">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-full shadow-sm">
                      <Sparkles className="w-5 h-5 text-purple-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">Genetik Füzyon Aktif</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Bebek yüzü, {motherImage && fatherImage ? 'anne ve baba' : motherImage ? 'anne' : 'baba'} fotoğrafından 
                        analiz edilen ten rengi, saç rengi, yüz şekli ve diğer genetik özellikler kullanılarak oluşturulacak.
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {!motherImage && !fatherImage && (
                <div className="bg-amber-50 rounded-xl p-4 border border-amber-100">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-white rounded-full shadow-sm">
                      <Heart className="w-5 h-5 text-amber-500" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-800 text-sm">Öneri: Anne-Baba Fotoğrafı Ekleyin</p>
                      <p className="text-xs text-slate-600 mt-1">
                        Anne ve baba fotoğrafları eklerseniz, bebek yüzü gerçek genetik özelliklere göre 
                        benzersiz şekilde oluşturulacak (ten rengi, saç, yüz şekli vb.)
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Patient Info */}
              {patient && (
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <p className="font-medium text-slate-800 flex items-center gap-2">
                    <Baby className="w-4 h-4 text-purple-500" />
                    Bebek Bilgileri
                  </p>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <span className="text-slate-500">Cinsiyet:</span>
                      <span className={`ml-2 font-medium ${patient.gender === 'female' ? 'text-pink-600' : patient.gender === 'male' ? 'text-blue-600' : 'text-slate-600'}`}>
                        {patient.gender === 'female' ? 'Kız' : patient.gender === 'male' ? 'Erkek' : 'Belirtilmemiş'}
                      </span>
                    </div>
                    <div>
                      <span className="text-slate-500">Hafta:</span>
                      <span className="ml-2 font-medium text-slate-800">
                        {patient.pregnancy_formatted || `${patient.pregnancy_week || '?'}w`}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Button */}
              <Button
                onClick={handleGenerateFace}
                disabled={generating}
                className="w-full h-14 bg-gradient-to-r from-[#5A67D8] via-purple-500 to-[#EC4899] hover:opacity-90 text-white text-lg font-semibold shadow-lg"
                data-testid="generate-face-button"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Oluşturuluyor...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    AI ile Bebek Yüzü Oluştur
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Right: Result Section */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-pink-50 via-purple-50 to-blue-50">
              <CardTitle className="text-lg text-slate-800 flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-500" />
                Oluşturulan Bebek Yüzü
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {generating ? (
                /* Scanning Animation */
                <div className="relative aspect-square bg-slate-900 rounded-2xl overflow-hidden flex items-center justify-center">
                  <ScanningEffect />
                  <div className="relative z-10 text-center space-y-4">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full border-4 border-[#5A67D8] border-t-transparent animate-spin mx-auto" />
                      <Sparkles className="w-8 h-8 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                    <p className="text-white font-medium animate-pulse">AI Bebek Yüzü Oluşturuluyor...</p>
                    <p className="text-slate-400 text-sm">Bu işlem birkaç saniye sürebilir</p>
                  </div>
                </div>
              ) : generatedImage ? (
                <>
                  {/* Family Result Display */}
                  <FamilyResult
                    motherImage={motherImage}
                    fatherImage={fatherImage}
                    babyImage={generatedImage}
                    geneticFeatures={geneticFeatures}
                    inheritedTraits={inheritedTraits}
                    onDownload={handleDownload}
                    onWhatsApp={handleWhatsApp}
                    onQRCode={() => setShowQRModal(true)}
                  />
                  {/* 4D Simulation Button — geçici olarak gizlendi */}
                  {false && (
                  <Button
                    onClick={() => {
                      sessionStorage.setItem('4d_source_image', generatedImage);
                      navigate('/doctor/4d-simulation?type=baby_face');
                    }}
                    className="w-full mt-4 bg-gradient-to-r from-cyan-500 to-purple-600 hover:from-cyan-600 hover:to-purple-700 text-white font-semibold"
                    size="lg"
                    data-testid="go-4d-simulation-button"
                  >
                    <Clapperboard className="w-5 h-5 mr-2" />
                    4D Simülasyon ile Canlandır
                  </Button>
                  )}
                  {/* 360° AR Button */}
                  <Button
                    onClick={() => {
                      sessionStorage.setItem('ar_360_image', generatedImage);
                      navigate('/doctor/ar-360');
                    }}
                    variant="outline"
                    className="w-full mt-2"
                    size="lg"
                    data-testid="go-ar360-button"
                  >
                    <Move3d className="w-5 h-5 mr-2" />
                    360° Görüntüle
                  </Button>
                </>
              ) : (
                /* Empty State */
                <div className="aspect-square bg-gradient-to-br from-slate-50 to-slate-100 rounded-2xl flex items-center justify-center">
                  <div className="text-center text-slate-400 space-y-4">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-gradient-to-br from-pink-100 to-blue-100 mx-auto flex items-center justify-center">
                        <Baby className="w-10 h-10 text-purple-300" />
                      </div>
                      <Sparkles className="w-6 h-6 text-purple-300 absolute -top-1 -right-1 animate-pulse" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-500">Bebek yüzü henüz oluşturulmadı</p>
                      <p className="text-sm mt-1">Sol taraftan görselleri yükleyip "Oluştur" butonuna tıklayın</p>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* QR Code Modal */}
      <QRCodeModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        imageUrl={generatedImage}
        patient={patient}
      />
    </DashboardLayout>
  );
};

export default AIFacePage;
